import * as React from "react";
import AdminStandardInput from "@gcp-artifact/compass/lib/components/updated/adminStandardInput";
import Button from "@gcp-artifact/compass/lib/components/updated/button";
import OTPInput from "react-otp-input";
import { DialogNew as Dialog } from "@gcp-artifact/compass/lib/patterns";
import api from "@/services/api/admin";
import { MixpanelEvents } from "@/services/mixpanel/events";
import useAdminStore from "@/services/store/useAdminStore";
import errorHandler from "@/utilities/errorHandler";
import translate from "@/utilities/i18n/getTranslate";
import { k } from "@/utilities/i18n";
import * as Styled from "./OTPContainerStyles";

interface IProps {
  callback: () => void;
  setOpenOTP: React.Dispatch<React.SetStateAction<boolean>>;
  title?: React.ReactNode;
  description?: React.ReactNode;
}

const OTPContainer = ({ callback, setOpenOTP, title, description }: IProps) => {
  const {
    adminNotification,
    adminStore: { setCurrentUser },
  } = useAdminStore();
  const [OTPCode, setOTPCode] = React.useState<string>("");
  const [isLoadingSpinner, setLoadingSpinner] = React.useState<boolean>(false);

  const NEW_OTP_REQUEST_DELAY_S = 60;
  const [timeLeft, setTimeLeft] = React.useState(NEW_OTP_REQUEST_DELAY_S);
  const requestOTPTimer = React.useRef<NodeJS.Timeout | undefined>(undefined);

  const DEFAULT_DESCRIPTION = <span>{translate(k.AUTH_otpDescription)}</span>;

  const DEFAULT_TITLE = translate(k.AUTH_otpTitle);

  const clearTimer = () => {
    if (requestOTPTimer.current) {
      clearInterval(requestOTPTimer.current);
      requestOTPTimer.current = undefined;
    }
  };

  const setupTimer = () => {
    clearTimer();

    setTimeLeft(NEW_OTP_REQUEST_DELAY_S);
    requestOTPTimer.current = setInterval(() => {
      setTimeLeft((prevTime) => {
        const nextTime = prevTime - 1;
        if (nextTime <= 0) {
          clearTimer();
          return 0;
        }
        return nextTime;
      });
    }, 1000);
  };

  const sendOTP = async () => {
    try {
      const res = await api.publicRequests.triggerEmailVerification({});
      if (res.meta.success) {
        // start the timer
        setupTimer();
        MixpanelEvents?.requestedOTPEvent();
      }
    } catch (err) {
      MixpanelEvents.errorEvent("Error Requesting OTP", { error: err });

      adminNotification.add({
        type: adminNotification.types.ERROR,
        timeoutToClose: 10,
        message: errorHandler(err),
      });

      //close otp window if there's error
      setOpenOTP(false);
    }
  };

  React.useEffect(() => {
    sendOTP();
  }, []);

  const getUserData = React.useCallback(async () => {
    try {
      const user = await api.profile.getUserProfile();
      setCurrentUser(user);
    } catch (err) {
      adminNotification.add({
        type: adminNotification.types.ERROR,
        timeoutToClose: 10,
        message: errorHandler(err),
      });
    }
  }, [adminNotification]);

  const verifyOTP = async () => {
    setLoadingSpinner(true);
    try {
      const res = await api.publicRequests.verifyEmail({
        otp: OTPCode,
      });
      if (res.meta.success) {
        setOpenOTP(false);
        callback?.();
        getUserData(); // refetch profile with updated details
      } else {
        MixpanelEvents.errorEvent("Error Verifying OTP", {
          error: res.meta,
        });
      }
    } catch (err) {
      MixpanelEvents.errorEvent("Error Verifying OTP", { error: err });

      adminNotification.add({
        type: adminNotification.types.ERROR,
        timeoutToClose: 10,
        message: errorHandler(err),
      });
    } finally {
      setOTPCode("");
      setLoadingSpinner(false);
    }
  };

  const handleCodeResend = () => {
    sendOTP();
    setupTimer();
  };

  return (
    <Dialog width="60rem" height="auto" maxWidth={"95vw"}>
      <Styled.Box>
        <Styled.Header>
          <Styled.Title>{title || DEFAULT_TITLE}</Styled.Title>
          <Styled.HeaderBackground viewBox="0 0 396 140" height="140" />
          <Styled.SubText>{description || DEFAULT_DESCRIPTION}</Styled.SubText>
        </Styled.Header>

        <Styled.Body>
          <OTPInput
            onChange={setOTPCode}
            value={OTPCode}
            renderInput={(props) => (
              <AdminStandardInput
                variant="primary-new"
                noMargin
                height="var(--otp-input-width)"
                {...props}
                isInput
              />
            )}
            numInputs={6}
            skipDefaultStyles
            renderSeparator={(i) => i === 2 && <Styled.OTPInputSeparator />}
            containerStyle={{
              display: "grid",
              marginBottom: "0.8rem",
              gridTemplateColumns: `repeat(3, var(--otp-input-width)) var(--space-between-inputs) repeat(3, var(--otp-input-width))`,
              gridAutoRows: `var(--otp-input-width)`,
              gap: ".8rem",
              justifyContent: "space-between",
            }}
            inputStyle={{
              textAlign: "center",
              fontSize: "var(--font-size-xl)",
            }}
            shouldAutoFocus
          />

          <Button
            onClick={handleCodeResend}
            disabled={!!timeLeft}
            variant="subtle-green"
            size="small"
            margin="0 !important"
          >
            {timeLeft
              ? translate(k.MIG_requestNewCodeIn, { time: timeLeft })
              : translate(k.MIG_resendCode)}
          </Button>

          <Styled.OTPButtons>
            <Button
              margin="0 !important"
              size="small"
              variant="secondary"
              onClick={() => setOpenOTP(false)}
              disabled={isLoadingSpinner || timeLeft !== 0}
            >
              {translate(k.A_cancel)}
            </Button>

            <Button
              margin="0 !important"
              type="submit"
              size="small"
              onClick={() => verifyOTP()}
              isPending={isLoadingSpinner}
              disabled={OTPCode.length < 6 || isLoadingSpinner}
              variant="primary"
            >
              {translate(k.A_continue)}
            </Button>
          </Styled.OTPButtons>
        </Styled.Body>
      </Styled.Box>
    </Dialog>
  );
};

export default OTPContainer;
